import { CalendarEvent } from '@/services/api-providers/CalendarServiceProvider';
import { PositionedEvent, EventsByDay, CalendarDay } from '@/@types/Calendar';

export const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const HOUR_HEIGHT = 60;

export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  // getDay: 0=Sun, 1=Mon, ... 6=Sat -> offset to Monday
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function formatDateKey(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getWeekDays(mondayDate: Date, weekdayNames: string[] = WEEKDAY_NAMES): CalendarDay[] {
  const today = new Date();
  const todayKey = formatDateKey(today);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mondayDate);
    d.setDate(d.getDate() + i);
    const dateKey = formatDateKey(d);
    return {
      dateKey,
      dayOfMonth: d.getDate(),
      weekday: weekdayNames[i],
      isToday: dateKey === todayKey,
      date: d,
    };
  });
}

export function getWeekLabel(start: Date, end: Date, monthNames: string[] = MONTH_NAMES): string {
  const startMonth = monthNames[start.getMonth()];
  const endMonth = monthNames[end.getMonth()];
  const startDay = start.getDate();
  const endDay = end.getDate();
  const year = end.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
}

export function eventLocalDate(event: CalendarEvent): string {
  return formatDateKey(new Date(event.startTime));
}

export function isAllDayEvent(event: CalendarEvent): boolean {
  if (event.isAllDay) return true;
  const start = new Date(event.startTime);
  const end = new Date(event.endTime);
  const durationMs = end.getTime() - start.getTime();
  const isStartMidnight = start.getHours() === 0 && start.getMinutes() === 0;
  return isStartMidnight && durationMs >= 24 * 60 * 60 * 1000;
}

export function calculateEventPosition(
  startTime: string,
  endTime: string,
  hourHeight: number = HOUR_HEIGHT
): { top: number; height: number } {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const pixelsPerMinute = hourHeight / 60;
  const top = startMinutes * pixelsPerMinute;
  const durationMinutes =
    endMinutes > startMinutes ? Math.max(endMinutes - startMinutes, 15) : Math.max(24 * 60 - startMinutes, 15);
  const height = durationMinutes * pixelsPerMinute;
  return { top, height };
}

export function groupEventsByDay(eventList: CalendarEvent[], days: CalendarDay[]): EventsByDay {
  const result: EventsByDay = {};
  for (const day of days) {
    result[day.dateKey] = [];
  }

  for (const ev of eventList) {
    const dateKey = eventLocalDate(ev);
    if (result[dateKey]) {
      const { top, height } = calculateEventPosition(ev.startTime, ev.endTime);
      result[dateKey].push({
        event: ev,
        top,
        height,
        columnIndex: 0,
        totalColumns: 1,
      });
    }
  }

  return result;
}

export function assignOverlapColumns(dayEvents: PositionedEvent[]): void {
  if (dayEvents.length === 0) return;

  // Sort by top (ascending), then by height (descending for ties)
  dayEvents.sort((a, b) => a.top - b.top || b.height - a.height);

  // Build overlap groups using sweep-line
  const groups: PositionedEvent[][] = [];
  let currentGroup: PositionedEvent[] = [dayEvents[0]];
  let groupEnd = dayEvents[0].top + dayEvents[0].height;

  for (let i = 1; i < dayEvents.length; i++) {
    const ev = dayEvents[i];
    if (ev.top < groupEnd) {
      currentGroup.push(ev);
      groupEnd = Math.max(groupEnd, ev.top + ev.height);
    } else {
      groups.push(currentGroup);
      currentGroup = [ev];
      groupEnd = ev.top + ev.height;
    }
  }
  groups.push(currentGroup);

  // Assign columns within each group
  for (const group of groups) {
    const columns: number[] = [];
    for (const ev of group) {
      let col = 0;
      while (col < columns.length && columns[col] > ev.top) {
        col++;
      }
      columns[col] = ev.top + ev.height;
      ev.columnIndex = col;
    }
    const totalCols = columns.length;
    for (const ev of group) {
      ev.totalColumns = totalCols;
    }
  }
}

export function formatTime(isoString: string): string {
  const date = new Date(isoString);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes} ${ampm}`;
}

export function formatHour(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}
